import { useEffect, useMemo, useState } from "react";
import QRCode from "qrcode";
import { Skeleton } from "@shared/ui/feedback/Skeleton";
import type { MemberInviteCredentials } from "./api";
import { groupPin } from "./visitorAccessFormat";

type CredentialRow = { method: string; qr_payload?: string | null };

function methodLabel(method: string): string {
  const map: Record<string, string> = {
    pin: "PIN",
    otp: "OTP",
    qr: "QR code",
  };
  return map[method.toLowerCase()] ?? method;
}

function buildCredentialRows(credentials: MemberInviteCredentials): CredentialRow[] {
  if (credentials.issued_credentials?.length) {
    return credentials.issued_credentials.filter((row) =>
      ["pin", "otp", "qr"].includes(row.method.toLowerCase()),
    );
  }
  const rows: CredentialRow[] = [];
  for (const method of credentials.credential_methods ?? []) {
    const lower = method.toLowerCase();
    if (!["pin", "otp", "qr"].includes(lower)) continue;
    rows.push({
      method: lower,
      qr_payload: lower === "qr" ? credentials.qr_payload ?? null : null,
    });
  }
  return rows;
}

function hasGateAuth(credentials: MemberInviteCredentials | undefined): boolean {
  if (!credentials) return false;
  const methods = credentials.credential_methods ?? [];
  return methods.some((m) => ["pin", "otp", "qr"].includes(m.toLowerCase()));
}

export function inviteCredentialsQueryKey(inviteId: string): readonly [string, string] {
  return ["access-invite-credentials", inviteId];
}

type InviteCredentialPassProps = {
  credentials: MemberInviteCredentials | undefined;
  loading?: boolean;
  /** The entry type's name, shown before the method: "Visitor PIN". */
  typeName?: string;
};

/**
 * Member portal visitor pass — shows PIN, OTP, and/or QR based on entry type auth methods.
 */
export function InviteCredentialPass({
  credentials,
  loading,
  typeName,
}: InviteCredentialPassProps): JSX.Element {
  const [qrUrls, setQrUrls] = useState<Record<string, string>>({});

  const rows = useMemo(
    () => (credentials ? buildCredentialRows(credentials) : []),
    [credentials],
  );

  useEffect(() => {
    if (!rows.length) {
      setQrUrls({});
      return;
    }
    let cancelled = false;
    void (async () => {
      const next: Record<string, string> = {};
      for (const row of rows) {
        if (row.method === "qr" && row.qr_payload) {
          try {
            next[row.method] = await QRCode.toDataURL(row.qr_payload, {
              margin: 1,
              width: 320,
            });
          } catch {
            /* skip */
          }
        }
      }
      if (!cancelled) setQrUrls(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [rows]);

  if (loading) {
    return (
      <div className="flex flex-col items-center gap-2">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-10 w-48" />
      </div>
    );
  }

  if (credentials?.cancelled) {
    return (
      <p className="text-center text-body-sm text-ink-secondary">
        This invite was cancelled. Gate credentials are no longer valid.
      </p>
    );
  }

  const pin = credentials?.pin ?? null;
  const showGateAuth = hasGateAuth(credentials);

  if (!showGateAuth || !rows.length) {
    return (
      <p className="text-center text-body-sm text-ink-secondary">
        No gate code for this invite type.
      </p>
    );
  }

  const methodChips = rows.map((row) => methodLabel(row.method));

  return (
    <>
      {methodChips.length > 1 ? (
        <p className="mb-4 text-center text-caption font-medium text-ink-muted">
          {methodChips.join(" · ")}
        </p>
      ) : null}

      <div className="flex w-full flex-col gap-4">
        {rows.map((row) => {
          const label = [typeName, methodLabel(row.method)].filter(Boolean).join(" ");

          if (row.method === "pin" || row.method === "otp") {
            return (
              <div key={row.method} className="flex flex-col items-center gap-1.5 text-center">
                <p className="text-[0.8125rem] font-semibold text-ink-secondary">{label}</p>
                {pin ? (
                  <p className="font-mono text-[2.375rem] font-semibold leading-tight tracking-[0.12em] text-ink">
                    {groupPin(pin)}
                  </p>
                ) : credentials?.pin_available ? (
                  <p className="mt-2 text-body-sm text-ink-secondary">
                    Code issued. Check the visitor notification.
                  </p>
                ) : (
                  <p className="mt-2 text-body-sm text-ink-secondary">
                    Gate code will appear once credentials are issued.
                  </p>
                )}
              </div>
            );
          }

          if (row.method === "qr") {
            return (
              <div key={row.method} className="flex flex-col items-center">
                {row.qr_payload && qrUrls.qr ? (
                  <img
                    src={qrUrls.qr}
                    alt={`${label} pass`}
                    className="h-56 w-56"
                  />
                ) : row.qr_payload ? (
                  <Skeleton className="h-56 w-56 rounded" />
                ) : (
                  <div className="flex h-56 w-56 items-center justify-center rounded bg-sunken px-4 text-center text-body-sm text-ink-secondary">
                    QR code will appear once credentials are issued.
                  </div>
                )}
                <p className="mt-3 text-[0.8125rem] font-semibold text-ink-secondary">{label}</p>
              </div>
            );
          }

          return null;
        })}
      </div>
    </>
  );
}
